/**
 * District Configuration Utility
 * Detects district code from subdomain or environment variable
 */

/**
 * Extract district code from subdomain
 * Examples:
 *   garland.example.com -> garland
 *   dallas.careersurvey.com -> dallas
 *   localhost -> null (use env variable)
 */
function extractDistrictFromSubdomain(hostname) {
  // Skip for localhost or IP addresses
  if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname) || hostname.startsWith('localhost:')) {
    return null;
  }

  // Skip for hosting platforms (Render, Heroku, Vercel, Netlify, etc.)
  if (hostname.includes('onrender.com') || hostname.includes('herokuapp.com') ||
      hostname.includes('vercel.app') || hostname.includes('netlify.app')) {
    return null;
  }

  // Extract first subdomain part
  const parts = hostname.split('.');

  // If hostname has at least 3 parts (subdomain.domain.tld), return first part
  if (parts.length >= 3) {
    return parts[0];
  }

  return null;
}

/**
 * Get current district code
 * Priority: URL Query Param > Subdomain > Environment Variable > default
 * @returns {string} District code (e.g., 'garland', 'dallas')
 */
export function getDistrictCode() {
  // Priority 1: Check URL query parameter (for local testing)
  // Example: ?district=garland
  const urlParams = new URLSearchParams(window.location.search);
  const queryDistrict = urlParams.get('district');
  if (queryDistrict) {
    console.log('[District] Detected from query param:', queryDistrict);
    return queryDistrict.toLowerCase();
  }

  // Priority 2: Check subdomain (for production)
  const hostname = window.location.hostname;
  const subdomainDistrict = extractDistrictFromSubdomain(hostname);
  if (subdomainDistrict) {
    console.log('[District] Detected from subdomain:', subdomainDistrict);
    return subdomainDistrict.toLowerCase();
  }

  // Priority 3: Use environment variable (for local development)
  const envDistrict = process.env.REACT_APP_DISTRICT_CODE;
  if (envDistrict) {
    console.log('[District] Using environment variable:', envDistrict);
    return envDistrict.toLowerCase();
  }

  // Default fallback
  console.log('[District] Using default: garland');
  return 'garland';
}

/**
 * Get district-specific API headers
 * @returns {Object} Headers object with district code
 */
export function getDistrictHeaders() {
  return {
    'X-District-Code': getDistrictCode()
  };
}

/**
 * Add district parameter to API URL
 * @param {string} url - The API URL
 * @returns {string} URL with district query parameter
 */
export function addDistrictToUrl(url) {
  // Check if district parameter already exists in URL
  if (url.includes('district=')) {
    return url; // Don't add duplicate district parameter
  }

  const districtCode = getDistrictCode();
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}district=${districtCode}`;
}

/**
 * Get environment mode
 * @returns {string} 'development' or 'production'
 */
export function getEnvironment() {
  return process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV || 'production';
}

/**
 * Check if in development mode
 * @returns {boolean}
 */
export function isDevelopment() {
  return getEnvironment() === 'development';
}

export default {
  getDistrictCode,
  getDistrictHeaders,
  addDistrictToUrl,
  getEnvironment,
  isDevelopment
};
